import { useEffect, useId, useMemo, useRef, useState, useCallback } from "react";
import type { KeyboardEvent } from "react";
import { cn } from "~/lib/utils";

/**
 * Lightweight combobox / typeahead.
 *
 * Design choices:
 *  - No external dep (cmdk, react-aria, downshift) — keeps the bundle slim
 *    and we don't need their advanced features (compound items, groups).
 *  - Async-friendly: `options` can change as the parent debounces the
 *    server-side search; we only render whatever is currently there.
 *  - Keyboard nav: ArrowDown/Up cycles, Enter selects, Esc closes,
 *    Tab closes without selecting.
 *  - The text input is the source of truth for the search term and is what
 *    we display when nothing has been selected yet — when a selection is
 *    made we render the selected option's label.
 *  - "loading" and "empty" states are exposed so the parent can wire them
 *    to a tRPC query and a helpful empty hint ("type at least 2 chars").
 *  - For accessibility we expose role="combobox", aria-expanded,
 *    aria-controls, aria-activedescendant — enough for screen readers to
 *    navigate the list with arrow keys.
 */
export interface ComboboxOption {
  id: string;
  slug: string;
  name: string;
  /** Optional secondary text shown muted to the right (e.g. country). */
  hint?: string;
}

interface ComboboxProps {
  value: ComboboxOption | null;
  onChange: (option: ComboboxOption | null) => void;
  /** Current query string (controlled — parent debounces & queries the server). */
  query: string;
  onQueryChange: (q: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Shown when there are zero options and we're not loading. */
  emptyHint?: string;
  /** Maximum height of the dropdown in px. Default 280. */
  maxHeight?: number;
  className?: string;
}

export function Combobox({
  value,
  onChange,
  query,
  onQueryChange,
  options,
  placeholder,
  disabled,
  loading,
  emptyHint,
  maxHeight = 280,
  className,
}: ComboboxProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  // Reset highlight when options list changes (debounced search returned).
  useEffect(() => {
    setActiveIdx(0);
  }, [options]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent): void {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const display = useMemo(() => {
    // When closed and a value is picked, show the canonical name; while
    // typing/open, show whatever the user typed (which is what they're
    // searching against).
    if (!open && value) return value.name;
    return query;
  }, [open, value, query]);

  const select = useCallback(
    (opt: ComboboxOption | null) => {
      onChange(opt);
      onQueryChange(opt?.name ?? "");
      setOpen(false);
      // Move focus back to the input so the user can keep tabbing.
      inputRef.current?.focus();
    },
    [onChange, onQueryChange],
  );

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => (options.length === 0 ? 0 : Math.min(i + 1, options.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      if (open && options[activeIdx]) {
        e.preventDefault();
        select(options[activeIdx]!);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "Tab") {
      // Tab without selecting — collapse the list.
      setOpen(false);
      return;
    }
    if (e.key === "Backspace" && value && query === value.name) {
      // The user starts editing a previously-selected value — clear the
      // selection so the parent re-runs its query against the empty string.
      onChange(null);
    }
  }

  // Ensure the active option is visible inside the scroll container.
  useEffect(() => {
    if (!open) return;
    const item = listRef.current?.querySelector<HTMLElement>(
      `[data-combobox-idx="${activeIdx}"]`,
    );
    if (item) item.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && options[activeIdx] ? `${listId}-${activeIdx}` : undefined}
        autoComplete="off"
        spellCheck={false}
        value={display}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          if (value) onChange(null);
          onQueryChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className={cn(
          "w-full h-10 px-3 text-[14px] rounded-[4px]",
          "bg-surface-card border border-[var(--divider-strong)] text-ink-primary",
          "placeholder:text-ink-tertiary",
          "focus:outline-none focus:border-[var(--accent)]",
          "transition-colors duration-[var(--dur-quick)]",
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
      />
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          style={{ maxHeight }}
          className={cn(
            "absolute z-30 mt-1 w-full overflow-y-auto",
            "rounded-[4px] border border-[var(--divider-strong)] bg-surface-card",
            "shadow-lg shadow-black/5",
          )}
        >
          {loading && options.length === 0 && (
            <li className="px-3 py-2 text-[12.5px] text-ink-tertiary">…</li>
          )}
          {!loading && options.length === 0 && (
            <li className="px-3 py-2 text-[12.5px] text-ink-tertiary">
              {emptyHint ?? "—"}
            </li>
          )}
          {options.map((opt, idx) => {
            const active = idx === activeIdx;
            const selected = value?.id === opt.id;
            return (
              <li
                key={opt.id}
                id={`${listId}-${idx}`}
                role="option"
                aria-selected={selected}
                data-combobox-idx={idx}
                onMouseDown={(e) => {
                  // mousedown (not click) so the input doesn't lose focus
                  // and re-trigger an unintended blur-close before select.
                  e.preventDefault();
                  select(opt);
                }}
                onMouseMove={() => setActiveIdx(idx)}
                className={cn(
                  "px-3 py-1.5 text-[13.5px] cursor-pointer flex items-center justify-between gap-3",
                  active ? "bg-surface-hover text-ink-primary" : "text-ink-secondary",
                  selected && "font-medium text-ink-primary",
                )}
              >
                <span className="truncate">{opt.name}</span>
                {opt.hint && (
                  <span className="mono text-[10.5px] uppercase tracking-[0.08em] text-ink-tertiary shrink-0">
                    {opt.hint}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Debounce hook tuned for autocomplete input: 180ms is short enough to
 * feel instant but cuts request volume by ~80% on real typing.
 */
export function useDebounced<T>(value: T, ms = 180): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
